import Chip from "@mui/material/Chip";
import type { ActionStatus, ActionPriority } from "@/lib/api";

const STATUS_COLORS: Record<ActionStatus, "info" | "warning" | "success"> = {
  open: "info",
  in_progress: "warning",
  done: "success",
};

const STATUS_LABELS: Record<ActionStatus, string> = {
  open: "Open",
  in_progress: "In Progress",
  done: "Done",
};

const PRIORITY_MAP: Record<ActionPriority, { color: string; bg: string }> = {
  low: { color: "#6b7280", bg: "#f3f4f6" },
  medium: { color: "#2563eb", bg: "#eff6ff" },
  high: { color: "#ea580c", bg: "#fff7ed" },
  critical: { color: "#dc2626", bg: "#fef2f2" },
};

const PRIORITY_LABELS: Record<ActionPriority, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  critical: "Critical",
};

export function StatusChip({ status }: { status: ActionStatus }) {
  return (
    <Chip
      label={STATUS_LABELS[status]}
      color={STATUS_COLORS[status]}
      size="small"
      variant="outlined"
    />
  );
}

export function PriorityChip({ priority }: { priority: ActionPriority }) {
  const { color, bg } = PRIORITY_MAP[priority];
  return (
    <Chip
      label={PRIORITY_LABELS[priority]}
      size="small"
      sx={{ color, bgcolor: bg, fontWeight: 600 }}
    />
  );
}

/** Returns the color for the highest-priority open action item. */
export function getActionBadgeColor(
  items: { status: ActionStatus; priority: ActionPriority }[],
): string | null {
  const open = items.filter((i) => i.status !== "done");
  if (open.length === 0) return null;
  const order: ActionPriority[] = ["critical", "high", "medium", "low"];
  for (const p of order) {
    if (open.some((i) => i.priority === p)) return PRIORITY_MAP[p].color;
  }
  return PRIORITY_MAP.medium.color;
}
